import React, { useState } from 'react';
import { Alert, Modal } from 'react-bootstrap';
import { ComponentWithReplacement } from '../../types';
import { Building } from '../../../../../types/protocols';
import { componentLocationLabel, componentLocationSubtitle } from '../../../../../Helpers/componentLocationLabel';
import type { ActivityCopyPlan } from '../../utils/copyActivities';

export interface CopyActivitiesSource {
    component: ComponentWithReplacement;
    /** Number of activities on the source */
    activityCount: number;
    /** What copying from this source does to the target */
    plan: ActivityCopyPlan;
}

interface CopyActivitiesModalProps {
    targetComponent: ComponentWithReplacement;
    sources: CopyActivitiesSource[];
    building: Building;
    getTranslation: (key: string | undefined) => string;
    getTemplateName: (templateId: string) => string;
    onApply: (source: CopyActivitiesSource) => void;
    onClose: () => void;
}

const PREVIEW_SECTIONS: { key: 'added' | 'replaced' | 'removed'; title: string }[] = [
    { key: 'added', title: 'Läggs till' },
    { key: 'replaced', title: 'Ersätts med källans inställningar' },
    { key: 'removed', title: 'Tas bort' },
];

/**
 * Modal for replacing a component's activities with those of another
 * component of the same base type. Pick a source, review what changes on
 * this component, then apply.
 */
export const CopyActivitiesModal: React.FC<CopyActivitiesModalProps> = ({
    targetComponent,
    sources,
    building,
    getTranslation,
    getTemplateName,
    onApply,
    onClose
}) => {
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const selected = sources.find(s => s.component.component.key === selectedKey) ?? null;

    return (
        <Modal show onHide={onClose} centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title as="h5">Kopiera aktiviteter</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-3">
                    Ersätt aktiviteterna på{' '}
                    <strong>{targetComponent.component.displayName(building)}</strong>{' '}
                    med aktiviteterna från:
                </p>
                <Alert variant="info" className="py-2 small">
                    Kontroller kopieras inte. De kopplas per komponent.
                </Alert>

                <div className="bulk-apply-list">
                    {sources.map(source => {
                        const component = source.component.component;
                        const location = componentLocationSubtitle(componentLocationLabel(component, building));
                        const details = [
                            getTranslation(component.specificType?.value ?? undefined),
                            location,
                            source.activityCount === 1 ? '1 aktivitet' : `${source.activityCount} aktiviteter`
                        ].filter(Boolean).join(' • ');

                        return (
                            <label key={component.key} className="bulk-apply-item">
                                <input
                                    type="radio"
                                    name="copy-activities-source"
                                    checked={selectedKey === component.key}
                                    onChange={() => setSelectedKey(component.key)}
                                />
                                <span className="bulk-apply-item-name">
                                    {component.displayName(building)}
                                    <span className="copy-activities-source-details">{details}</span>
                                </span>
                            </label>
                        );
                    })}
                </div>

                {selected && (
                    <div className="copy-activities-preview">
                        {PREVIEW_SECTIONS.map(({ key, title }) => {
                            const templateIds = selected.plan[key];
                            if (templateIds.length === 0) return null;
                            return (
                                <div key={key} className="copy-activities-preview-section">
                                    <div className="copy-activities-preview-title">{title} ({templateIds.length})</div>
                                    <ul>
                                        {templateIds.map(id => <li key={id}>{getTemplateName(id)}</li>)}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                    Avbryt
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => selected && onApply(selected)}
                    disabled={!selected}
                >
                    Ersätt aktiviteter
                </button>
            </Modal.Footer>
        </Modal>
    );
};
